import { createContext, useContext } from "react"

import { ComputeFacetsResponse, ZenoClient } from "@vertesia/client"
import { ComplexSearchPayload, ComplexSearchQuery, ContentObjectItem, FacetBucket, FacetSpec } from '@vertesia/common'

import { SharedState, useWatchSharedState } from "@hooks/use-shared-state"

export const PAGE_SIZE = 100

interface DocumentSearchResult {
    objects: ContentObjectItem[]
    error?: Error
    isSearching: boolean
    hasMore: boolean
}
export class DocumentSearch {
    collectionId?: string
    facets = new SharedState<ComputeFacetsResponse>({})
    result = new SharedState<DocumentSearchResult>({ objects: [], isSearching: false, hasMore: true })

    facetSpecs: FacetSpec[] = []
    query: ComplexSearchQuery = {}

    constructor(
        public client: ZenoClient,
        public limit = PAGE_SIZE
    ) {
        this.result = new SharedState<DocumentSearchResult>({
            objects: [],
            isSearching: false,
            hasMore: true
        })
    }

    withFacets(facets: FacetSpec[]) {
        this.facetSpecs = facets
        return this
    }

    get objects() {
        return this.result.value.objects
    }

    get error() {
        return this.result.value.error
    }

    get isRunning() {
        return this.result.value.isSearching
    }

    get hasMore() {
        return this.result.value.hasMore
    }

    getFilterValue(name: string) {
        return (this.query as any)[name]
    }

    setFilterValue(name: string, value: any) {
        (this.query as any)[name] = value
        this.search()
    }

    clearFilters(autoSearch: boolean = true) {
        const parent = this.query.parent
        this.query = {
            parent
        }

        if (autoSearch) {
            this.search()
        }
    }

    getFacetBuckets(name: string): FacetBucket[] {
        return (this.facets.value as any)[name]?.buckets || []
    }

    resetFacets() {
        this.query = {}
    }

    reset(isSearching = true) {
        this.result.value = {
            objects: [],
            isSearching,
            error: undefined,
            hasMore: true
        }
    }

    _updateRunningState(value: boolean) {
        this.result.value = {
            objects: this.objects,
            isSearching: value,
            error: this.error,
            hasMore: this.hasMore
        }
    }

    _searchRequest(query: ComplexSearchQuery, limit: number, offset: number, includeFacets: boolean = true) {
        const payload: ComplexSearchPayload = {
            limit,
            offset,
            query,
            facets: includeFacets ? this.facetSpecs : undefined
        }

        return this.collectionId ?
            this.client.collections.searchMembers(this.collectionId, payload)
            : this.client.objects.search(payload)
    }

    async _search(loadMore = false) {
        if (this.isRunning) {
            return Promise.resolve(false)
        }

        this.result.value = {
            isSearching: true,
            objects: loadMore ? this.objects : [],
            error: undefined,
            hasMore: true
        }

        const limit = this.limit
        const offset = this.objects.length

        try {
            const res = await this._searchRequest(this.query, limit, offset, true)
            const results = res.results || []
            const facets = res.facets || {}

            this.result.value = {
                isSearching: false,
                objects: loadMore ? this.objects.concat(results) : results,
                hasMore: results.length === limit
            }

            this.facets.value = facets
            return true
        } catch (err) {
            this.result.value = {
                error: err instanceof Error ? err : new Error(String(err)),
                isSearching: false,
                objects: this.objects,
                hasMore: this.hasMore
            }
            throw err
        }
    }

    search() {
        if (this.isRunning) {
            return Promise.resolve(false)
        }
        return this._search(false)
    }

    loadMore() {
        if (this.isRunning) {
            return Promise.resolve(false)
        }
        if (this.query.vector) {
            return Promise.resolve(false)
        }
        return this._search(true)
    }
}

export const DocumentSearchContext = createContext<DocumentSearch | undefined>(undefined)

export function useDocumentSearch() {
    return useContext(DocumentSearchContext)!
}

export function useWatchDocumentSearchFacets() {
    return useWatchSharedState(useDocumentSearch().facets)
}

export function useWatchDocumentSearchResult() {
    const search = useDocumentSearch()
    const result = useWatchSharedState(search.result)
    return { ...result, search }
}

export function useDocumentSearchCount() {
    const search = useDocumentSearch()
    const result = useWatchSharedState(search.facets)
    return result.total
}
